import type { Connection } from "@/store/site";
import type { ConnectionState } from "@/data/types";

/**
 * Pure, presentation-only freshness projection (G29).
 *
 * Collapses the store's 6-value `connection` union and the snapshot's
 * 3-value {@link ConnectionState} into one of four tone BUCKETS, then maps
 * each bucket to a shared tone class string. The point is a single tone
 * vocabulary: the Topbar data-source pill and the TelemetryOutageStrip
 * route their degraded-state colours through this function so the two
 * surfaces can never drift apart on what "amber" vs "red" means.
 *
 * PRESENTATION-ONLY: this touches no store state and derives nothing the
 * rest of the app reads — every existing internal connection/snapshot
 * state is preserved exactly. It only computes a tone.
 *
 * It never owns the DEMO tone: for `connection === "demo"` it reports the
 * bucket ("simulated") but deliberately returns NO tone, so the caller's
 * demo colour (G30's warn tone, set in Topbar) is never overridden here.
 * No React/store-value imports — types only — so it stays unit-testable
 * and free of import cycles.
 */
export type FreshnessBucket = "live" | "aging" | "down" | "simulated";

export interface BucketView {
  bucket: FreshnessBucket;
  /**
   * Tone class string for the bucket, or `undefined` for demo — the demo
   * tone stays the caller's responsibility (G30) and must not be set here.
   */
  tone: string | undefined;
}

const TONE: Record<FreshnessBucket, string> = {
  live: "text-ok border-ok/30 bg-ok/10",
  aging: "text-warn border-warn/30 bg-warn/10",
  down: "text-critical border-critical/30 bg-critical/10",
  simulated: "text-text-muted border-edge bg-surface-2",
};

export function freshnessBucket(args: {
  connection: Connection;
  snapshot: ConnectionState;
  hasError: boolean;
}): BucketView {
  const { connection, snapshot, hasError } = args;

  // Demo: report the simulated bucket but emit NO tone — G30 owns the demo
  // colour (warn), and this function must never override it.
  if (connection === "demo") return { bucket: "simulated", tone: undefined };

  // Hard failure first.
  if (hasError || connection === "error") return { bucket: "down", tone: TONE.down };

  // Anything degraded-but-not-failed reads as aging.
  if (
    connection === "reconnecting" ||
    connection === "connecting" ||
    snapshot === "offline" ||
    snapshot === "stale"
  ) {
    return { bucket: "aging", tone: TONE.aging };
  }

  // Live store state with a live snapshot is the only truly-live case.
  if (connection === "live" && snapshot === "live") return { bucket: "live", tone: TONE.live };

  // mock (and any residual case) is simulated.
  return { bucket: "simulated", tone: TONE.simulated };
}
